/** 二维仿射矩阵与基本变换。
 *
 * 矩阵按 SVG/Canvas 约定存储为 [a, b, c, d, e, f]：
 *   x' = a·x + c·y + e
 *   y' = b·x + d·y + f
 *
 * 折法展开必须使用**反射**而不是旋转：折叠一次即关于折线镜像。
 * 误用旋转会让非对称剪口的展开方向错误（见 docs/fold-geometry.md 1.1）。
 */
import type { Vec } from '../types';

export type Mat = readonly [number, number, number, number, number, number];

export const IDENTITY: Mat = [1, 0, 0, 1, 0, 0];

export function apply(m: Mat, p: Vec): Vec {
  return { x: m[0] * p.x + m[2] * p.y + m[4], y: m[1] * p.x + m[3] * p.y + m[5] };
}

/** 先应用 inner，再应用 outer。 */
export function multiply(outer: Mat, inner: Mat): Mat {
  const [a1, b1, c1, d1, e1, f1] = outer;
  const [a2, b2, c2, d2, e2, f2] = inner;
  return [
    a1 * a2 + c1 * b2,
    b1 * a2 + d1 * b2,
    a1 * c2 + c1 * d2,
    b1 * c2 + d1 * d2,
    a1 * e2 + c1 * f2 + e1,
    b1 * e2 + d1 * f2 + f1,
  ];
}

export function determinant(m: Mat): number {
  return m[0] * m[3] - m[1] * m[2];
}

export function rotation(angleRad: number): Mat {
  const c = Math.cos(angleRad);
  const s = Math.sin(angleRad);
  return [c, s, -s, c, 0, 0];
}

export function translation(dx: number, dy: number): Mat {
  return [1, 0, 0, 1, dx, dy];
}

/**
 * 关于过原点、方向角为 theta 的直线做反射。
 * 矩阵为 [[cos2θ, sin2θ], [sin2θ, -cos2θ]]，行列式恒为 -1。
 */
export function reflectAbout(angleRad: number): Mat {
  const c2 = Math.cos(2 * angleRad);
  const s2 = Math.sin(2 * angleRad);
  // [a, b, c, d, e, f] 对应 x' = c2·x + s2·y，y' = s2·x - c2·y
  return [c2, s2, s2, -c2, 0, 0];
}

/** 关于过原点、角度为 theta 的直线所在**无向线**反射，与方向相差 π 等价。 */
export function reflectAboutDeg(angleDeg: number): Mat {
  return reflectAbout((angleDeg * Math.PI) / 180);
}

/** 行列为 ±1 的手性判断，用于测试不变量 I2。 */
export function handedness(m: Mat): 1 | -1 {
  return determinant(m) >= 0 ? 1 : -1;
}
